import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import customerService from "../Services/customerService";
import supplierService from "../Services/supplierService";
import uploadService from "../Services/uploadService";
import { LoadingDots } from "../Components/LoadingDots";
import Dropdown from "../Components/Dropdown";
import documentService from "../Services/documentService";
import DatePicker from "../Components/DatePicker";
import SecondaryButton from "../Components/SecondaryButton";
import { MdOutlineAdd } from "react-icons/md";

const AddDocument = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState();
  const [responseError, setResponseError] = useState("");
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [files, setFiles] = useState({
    docfile: null,
    pack_list: null,
    delivery_note: null,
    payment_receipt: null,
    custom_doc: null,
  });

  const [data, setData] = useState({
    title: "",
    role: "",
    customer_supplier: "",
    note: "",
    date: "",
    docfile: "",
    pack_list: "",
    delivery_note: "",
    payment_receipt: "",
    custom_doc: "",
    amount: "",
  });

  const [errors, setErrors] = useState({
    title: false,
    role: false,
    customer_supplier: false,
    note: false,
    date: false,
    docfile: false,
    pack_list: false,
    delivery_note: false,
    payment_receipt: false,
    amount: false,
  });

  const [regex, setRegex] = useState({
    title: /^.{1,100}$/,
    role: /^(Customer|Supplier)$/,
    customer_supplier: /^[0-9]+$/,
    note: /^.{1,100}$/,
    date: /^\d{4}-\d{2}-\d{2}$/,
    docfile: /.{1,50}$/,
    pack_list: /.{1,50}$/,
    delivery_note: /.{1,50}$/,
    payment_receipt: /.{1,50}$/,
    custom_doc: /.{1,50}$/,
    amount: /.{1,50}$/,
  });

  useEffect(() => {
    const getCustomers = async () => {
      try {
        const response = await customerService.fetchAll();
        setCustomers(response.customers);
      } catch (error) {
        console.log(error);
      }
    };
    getCustomers();
  }, []);

  useEffect(() => {
    const getSuppliers = async () => {
      try {
        const response = await supplierService.fetchAll();
        setSuppliers(response.suppliers);
      } catch (error) {
        console.log(error);
      }
    };
    getSuppliers();
  }, []);

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const dropdownRef = useRef();
  const options = [
    { label: "Cash", value: "cash" },
    { label: "Credit", value: "credit" },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsTermsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (v, n) => {
    setResponseError("");
    setData((d) => ({ ...d, [n]: v }));
    if (v.length > 0) {
      setErrors((prevState) => ({ ...prevState, [n]: false }));
    }
  };

  const convertToBase64 = (e, field) => {
    const reader = new FileReader();
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      reader.readAsDataURL(selectedFile);
      reader.onload = () => {
        setErrors((errors) => ({ ...errors, [field]: false }));
        setFiles((prev) => ({ ...prev, [field]: selectedFile })); // save actual file
        setData((data) => ({ ...data, [field]: reader.result })); // preview/base64
      };
    }
  };

  const handleSubmit = async () => {
    let hasError = false;

    Object.keys(data).forEach((v) => {
      const value = String(data[v] ?? "");
      const pattern = regex[v];
      const isValid = pattern?.test(value);

      if (!isValid) {
        setErrors((prevState) => ({ ...prevState, [v]: true }));
        hasError = true;
      }
    });

    if (hasError) {
      toast.error("Please fill the form correctly", { id: "1" });
      return;
    }

    setLoading(true);

    try {
      // upload each file separately
      const uploadedUrls = {};

      for (const field of Object.keys(files)) {
        if (files[field]) {
          const res = await uploadService.single(files[field]);
          uploadedUrls[field] = res.response.url;
        }
      }

      let form = {
        ...data,
        role: data.role === "Customer" ? 1 : data.role === "Supplier" ? 2 : "",
        ...uploadedUrls, // overwrite docfile, pack_list, etc with uploaded URLs
      };

      const res = await documentService.create(form);
      setLoading(false);

      if (res.responseCode === 409) {
        toast.error("Email already used");
        setResponseError(res.message);
      } else {
        toast.success("Document Added Successfully", { id: "2" });
        navigate("/document");
      }
    } catch (err) {
      setLoading(false);
      toast.error("Upload failed. Try again.");
      console.error(err);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  const dropdownOption = ["Customer", "Supplier"].map((option) => ({
    label: option,
    value: option,
  }));
  return (
    <main className="mx-10 mt-2 p-6 rounded-3xl border border-gray-200 shadow-lg bg-white min-h-[calc(100vh-48px)]">
      <p className="font-bold text-3xl text-secondary text-center mt-10">
        Add Document
      </p>

      <div className="mt-10 px-20 xl:px-80">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4">
          {/* Title */}
          <div>
            <label className={`primary-label`}>Title</label>
            <input
              value={data.title}
              name="title"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              onKeyDown={handleKeyDown}
              placeholder="Add title"
              className={`primary-input ${
                errors["title"] ? "border-red-600" : ""
              }`}
            />
          </div>

          {/* Role */}
          <div className="relative w-full">
            <label className="primary-label mb-1">Role</label>
            <Dropdown
              selected={data.role}
              placeholder="Select Role"
              options={dropdownOption}
              onChange={(value) => handleChange(value, "role")}
            />
          </div>

          {/* Customer / Supplier */}
          <div className="relative w-full">
            <label className="primary-label mb-1">
              {data.role === "Supplier"
                ? "Supplier"
                : data.role === "Customer"
                  ? "Customer"
                  : "Supplier / Customer"}
            </label>
            <Dropdown
              selected={data.customer_supplier}
              placeholder={`Select ${
                data.role === "Supplier" ? "Supplier" : "Customer"
              }`}
              options={
                data.role === "Supplier"
                  ? suppliers.map((s) => ({ label: s.name, value: s.id }))
                  : customers.map((c) => ({
                      label: c.company_name,
                      value: c.id,
                    }))
              }
              onChange={(value) => handleChange(value, "customer_supplier")}
            />
          </div>

          {/* Note */}
          <div>
            <label className={`primary-label`}>Note</label>
            <input
              value={data.note}
              name="note"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Add Note"
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["note"] ? "border-red-600" : "border-gray-300"
              }`}
            />
          </div>
          {/* Amount  */}
          <div>
            <label className={`primary-label`}>Amount</label>
            <input
              value={data.amount}
              name="amount"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              onKeyDown={handleKeyDown}
              placeholder="Add Amount"
              className={`primary-input ${
                errors["amount"] ? "border-red-600" : ""
              }`}
            />
          </div>

          {/* Date */}
          <div>
            <label className={`primary-label`}>Date</label>
            <input
              value={data.date}
              name="date"
              type="date"
              onChange={(e) => handleChange(e.target.value, e.target.name)}
              placeholder="Date"
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["date"] ? "border-red-600" : "border-gray-300"
              }`}
            />
          </div>

          {/* Upload Document */}
          <div className="col-span-2">
            <label className={`primary-label`}>Upload Document</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              placeholder="Upload File"
              onChange={(e) => convertToBase64(e, "docfile")}
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["docfile"] ? "border-red-600" : "border-gray-300"
              }
                file:mr-4 file:px-6 file:rounded-full
                file:border-0 file:text-[12px] file:font-semibold
                file:bg-white file:text-black
                hover:file:bg-primary hover:file:text-white`}
            />
          </div>

          {/* PackList */}
          <div className="col-span-1">
            <label className={`primary-label`}>Pack List</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              placeholder="Upload File"
              onChange={(e) => convertToBase64(e, "pack_list")}
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["pack_list"] ? "border-red-600" : "border-gray-300"
              }
                file:mr-4 file:px-6 file:rounded-full
                file:border-0 file:text-[12px] file:font-semibold
                file:bg-white file:text-black
                hover:file:bg-primary hover:file:text-white`}
            />
          </div>

          {/* Delivery Note */}
          <div className="col-span-1">
            <label className={`primary-label`}>Delivery Note</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              placeholder="Upload File"
              onChange={(e) => convertToBase64(e, "delivery_note")}
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["delivery_note"] ? "border-red-600" : "border-gray-300"
              }
                file:mr-4 file:px-6 file:rounded-full
                file:border-0 file:text-[12px] file:font-semibold
                file:bg-white file:text-black
                hover:file:bg-primary hover:file:text-white`}
            />
          </div>

          {/* Payment Received */}
          <div className="col-span-1">
            <label className={`primary-label`}>Payment Receipt</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              placeholder="Upload File"
              onChange={(e) => convertToBase64(e, "payment_receipt")}
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["payment_receipt"] ? "border-red-600" : "border-gray-300"
              }
                file:mr-4 file:px-6 file:rounded-full
                file:border-0 file:text-[12px] file:font-semibold
                file:bg-white file:text-black
                hover:file:bg-primary hover:file:text-white`}
            />
          </div>
          {/* Custome Doc */}
          <div className="col-span-1">
            <label className={`primary-label`}>Custom Doc</label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              placeholder="Upload File"
              onChange={(e) => convertToBase64(e, "custom_doc")}
              onKeyDown={handleKeyDown}
              className={`primary-input ${
                errors["custom_doc"] ? "border-red-600" : "border-gray-300"
              }
                file:mr-4 file:px-6 file:rounded-full
                file:border-0 file:text-[12px] file:font-semibold
                file:bg-white file:text-black
                hover:file:bg-primary hover:file:text-white`}
            />
          </div>
        </div>

        {/* Response error */}
        <div>
          {responseError ? (
            <div className="text-red-600 text-sm">{responseError}</div>
          ) : (
            ""
          )}
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-center flex-col mt-12">
          <div className="flex justify-center mt-10">
            <SecondaryButton
              text={loading ? <LoadingDots /> : "Add Document"}
              Icon={MdOutlineAdd}
              onClick={() => handleSubmit()}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default AddDocument;
